import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Pencil,
  Trash2,
  Plus,
  ArrowRight,
  Volume2,
  Sheet as SheetIcon,
  ExternalLink,
  RefreshCw,
  Lock,
  AlertTriangle
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { QuestionForm } from '@/components/QuestionForm';
import {
  fetchJson,
  type Questionnaire,
  type Question,
  type InputType,
  type SheetStatus,
  type SheetAccess
} from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatDateTime } from '@/lib/utils';

function SheetSyncCard({ qnId, initial }: { qnId: string; initial?: SheetStatus }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SheetStatus | undefined>(initial);
  const [access, setAccess] = useState<SheetAccess | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // Probe whether the Drive file still exists/is reachable (only meaningful when
  // sync is on and a file was already created). Separate cheap endpoint so the
  // main status load - and the questionnaire list - stay off the Google API.
  const checkAccess = useCallback(
    async (s?: SheetStatus) => {
      if (!s?.enabled || !s.sheetId) {
        setAccess(undefined);
        return;
      }
      try {
        setAccess(await fetchJson<SheetAccess>(`/api/questionnaires/${qnId}/sheet/access`));
      } catch {
        setAccess(undefined); // a probe failure is not proof the file is gone
      }
    },
    [qnId]
  );

  const refresh = useCallback(async () => {
    const s = await fetchJson<SheetStatus>(`/api/questionnaires/${qnId}/sheet`);
    setStatus(s);
    await checkAccess(s);
  }, [qnId, checkAccess]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (status && !status.connected) {
    return (
      <Card>
        <CardHeader className="flex-none">
          <CardTitle className="flex items-center gap-2 text-base">
            <SheetIcon className="h-4 w-4" />
            סנכרון ל-Google Sheets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            כדי להפעיל סנכרון אוטומטי ל-Google Sheets, חברו תחילה חשבון Google לארגון. הקבצים ייווצרו
            ב-Drive של אותו חשבון.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
            חברו חשבון Google בהגדרות
          </Button>
        </CardContent>
      </Card>
    );
  }

  const enabled = !!status?.enabled;
  const inaccessible = enabled && !!access && !access.accessible;
  const accessMessage =
    access?.reason === 'trashed'
      ? 'הקובץ הועבר לאשפה ב-Google Drive.'
      : access?.reason === 'missing'
        ? 'הקובץ נמחק או שאין יותר הרשאת גישה אליו.'
        : 'הקובץ המקושר אינו נגיש יותר.';

  async function toggle(on: boolean) {
    setBusy(true);
    try {
      if (on) {
        const s = await fetchJson<SheetStatus>(`/api/questionnaires/${qnId}/sheet`, {
          method: 'POST'
        });
        setStatus(s);
        await checkAccess(s);
        toast.success('הסנכרון הופעל - נוצר קובץ ייעודי והנתונים הקיימים הועברו');
      } else {
        await fetchJson(`/api/questionnaires/${qnId}/sheet`, { method: 'DELETE' });
        await refresh();
        toast.success('הסנכרון הושבת (הקובץ הקיים נשמר)');
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(
        e.message === 'not_connected'
          ? 'אין חשבון Google מחובר לארגון - חברו חשבון בהגדרות'
          : e.message === 'sheet_setup_failed'
            ? 'יצירת הקובץ נכשלה - נסו לחבר מחדש את חשבון Google'
            : e.message || 'שגיאה'
      );
    } finally {
      setBusy(false);
    }
  }

  async function resync() {
    setBusy(true);
    try {
      const s = await fetchJson<SheetStatus>(`/api/questionnaires/${qnId}/sheet/resync`, {
        method: 'POST'
      });
      setStatus(s);
      await checkAccess(s);
      toast.success('סונכרן מחדש');
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e.message || 'שגיאה בסנכרון מחדש');
    } finally {
      setBusy(false);
    }
  }

  async function recreate() {
    setBusy(true);
    try {
      const s = await fetchJson<SheetStatus>(`/api/questionnaires/${qnId}/sheet/recreate`, {
        method: 'POST'
      });
      setStatus(s);
      await checkAccess(s);
      toast.success('נוצר קובץ חדש וכל ההיסטוריה הועברה אליו');
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(
        e.message === 'not_connected'
          ? 'אין חשבון Google מחובר לארגון - חברו חשבון בהגדרות'
          : e.message === 'recreate_failed'
            ? 'יצירת הקובץ החדש נכשלה - נסו שוב או חברו מחדש את חשבון Google'
            : e.message || 'שגיאה ביצירת קובץ חדש'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-none">
        <CardTitle className="flex items-center gap-2 text-base">
          <SheetIcon className="h-4 w-4" />
          סנכרון ל-Google Sheets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm">
              העברה אוטומטית של כל רשומה חדשה לקובץ Google Sheets ייעודי לשאלון זה.
            </p>
            <p className="text-xs text-muted-foreground">
              מנהלי הארגון מקבלים גישת עריכה אוטומטית. לשונית הנתונים הגולמיים נעולה
              <Lock className="mx-1 inline h-3 w-3" />
              לעריכה - לניתוח צרו לשונית חדשה משלכם.
            </p>
          </div>
          <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} />
        </div>

        {inaccessible && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-destructive" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">{accessMessage}</p>
                <p className="text-xs text-muted-foreground">
                  הסנכרון האוטומטי אינו פעיל כל עוד הקובץ חסר. אפשר ליצור קובץ חדש - כל
                  ההיסטוריה של השאלון תועבר אליו אוטומטית.
                </p>
              </div>
            </div>
            <Button variant="destructive" size="sm" disabled={busy} onClick={recreate}>
              <RefreshCw className="h-4 w-4" />
              צרו קובץ חדש
            </Button>
          </div>
        )}

        {enabled && !inaccessible && status?.url && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={status.url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                פתח את הקובץ
              </a>
            </Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={resync}>
              <RefreshCw className="h-4 w-4" />
              סנכרן מחדש
            </Button>
            {status.lastSyncAt && (
              <span className="text-xs text-muted-foreground">
                סונכרן לאחרונה: {formatDateTime(status.lastSyncAt)}
              </span>
            )}
          </div>
        )}

        {status?.lastError && (
          <p className="text-xs text-destructive" dir="ltr">
            שגיאת סנכרון אחרונה: {status.lastError}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const TYPE_BADGE: Record<InputType, { label: string; cls: string }> = {
  tap: { label: 'הקשה', cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  stt: { label: 'דיבור', cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30' },
  announcement: { label: 'השמעה', cls: 'bg-muted text-muted-foreground border-border' }
};

export default function QuestionnaireEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [qn, setQn] = useState<Questionnaire | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Question | null>(null);
  const [creating, setCreating] = useState(false);

  async function handleReorder(ids: string[]) {
    const byId = new Map(questions.map((q) => [q.id, q]));
    const reordered = ids.map((qid) => byId.get(qid)).filter((q): q is Question => !!q);
    setQuestions(reordered); // optimistic
    try {
      await fetchJson(`/api/questionnaires/${id}/questions/reorder`, {
        method: 'POST',
        body: JSON.stringify({ ids })
      });
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e.message || 'שגיאה בשמירת הסדר');
      refresh();
    }
  }

  async function handleDeleteQuestion(q: Question) {
    const ok = await confirm({
      title: `למחוק את השאלה "${q.label}"?`,
      description: 'אם יש answers היסטוריים, ניתן רק להשבית את השאלה.',
      confirmText: 'מחק',
      variant: 'destructive'
    });
    if (!ok) return;
    try {
      await fetchJson(`/api/questions/${q.id}`, { method: 'DELETE' });
      toast.success('נמחק');
      refresh();
    } catch (err: unknown) {
      const e = err as { message?: string };
      if (e.message === 'has_answers') {
        toast.error('יש answers היסטוריים. השבת במקום למחוק.');
      } else {
        toast.error(e.message || 'שגיאה');
      }
    }
  }

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const all = await fetchJson<Questionnaire[]>('/api/questionnaires');
      const found = all.find((q) => q.id === id);
      if (!found) {
        toast.error('שאלון לא נמצא');
        navigate('/questionnaires');
        return;
      }
      setQn(found);
      const qs = await fetchJson<Question[]>(`/api/questionnaires/${id}/questions`);
      setQuestions(qs);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: DataTableColumn<Question>[] = useMemo(
    () => [
      {
        key: 'order',
        header: 'סדר',
        cell: (q) => (
          <span className="text-muted-foreground">
            {questions.findIndex((x) => x.id === q.id) + 1}
          </span>
        ),
        exportValue: (q) => questions.findIndex((x) => x.id === q.id) + 1
      },
      {
        key: 'label',
        header: 'תווית',
        cell: (q) => (
          <div className="flex items-center gap-2 font-medium">
            {q.inputType === 'announcement' && (
              <Volume2 className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            {q.label}
          </div>
        ),
        exportValue: (q) => q.label
      },
      {
        key: 'key',
        header: 'מפתח',
        cell: (q) => (
          <span className="font-mono text-xs" dir="ltr">
            {q.key}
          </span>
        ),
        exportValue: (q) => q.key
      },
      {
        key: 'type',
        header: 'סוג',
        cell: (q) => (
          <Badge className={TYPE_BADGE[q.inputType].cls} variant="outline">
            {TYPE_BADGE[q.inputType].label}
          </Badge>
        ),
        exportValue: (q) => TYPE_BADGE[q.inputType].label
      },
      {
        key: 'required',
        header: 'חובה',
        cell: (q) => (q.inputType !== 'announcement' ? (q.isRequired ? 'כן' : 'לא') : ''),
        exportValue: (q) => (q.inputType !== 'announcement' ? (q.isRequired ? 'כן' : 'לא') : '')
      },
      {
        key: 'status',
        header: 'סטטוס',
        cell: (q) => (
          <Badge variant={q.isActive ? 'success' : 'muted'}>{q.isActive ? 'פעיל' : 'מושבת'}</Badge>
        ),
        exportValue: (q) => (q.isActive ? 'פעיל' : 'מושבת')
      },
      {
        key: 'actions',
        header: 'פעולות',
        align: 'end',
        exportable: false,
        stopClickPropagation: true,
        cell: (q) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" onClick={() => setEditing(q)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDeleteQuestion(q)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions]
  );

  if (loading || !qn) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/questionnaires')}>
          <ArrowRight className="h-4 w-4" />
          חזרה לשאלונים
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{qn.name}</h1>
          <p className="text-sm text-muted-foreground">
            {qn.ivrName} · {questions.filter((q) => q.isActive).length} שאלות פעילות
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          הוסף שאלה
        </Button>
      </div>

      <SheetSyncCard qnId={qn.id} initial={qn.sheet} />

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="flex-none">
          <CardTitle>שאלות</CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={columns}
            data={questions}
            getRowKey={(q) => q.id}
            emptyMessage="עדיין אין שאלות. הוסף שאלה ראשונה כדי להתחיל לאסוף נתונים."
            exportFileName={`שאלות-${qn.name}`}
            exportSheetName="שאלות"
            rowClassName={(q) =>
              [!q.isActive && 'opacity-50', q.inputType === 'announcement' && 'bg-muted/30']
                .filter(Boolean)
                .join(' ') || undefined
            }
            dnd={{ onReorder: handleReorder }}
          />
        </CardContent>
      </Card>

      <Dialog
        open={creating || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'עריכת שאלה' : 'הוספת שאלה'}</DialogTitle>
          </DialogHeader>
          <QuestionForm
            initial={editing || undefined}
            onCancel={() => {
              setCreating(false);
              setEditing(null);
            }}
            onSave={async (data) => {
              try {
                if (editing) {
                  await fetchJson(`/api/questions/${editing.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(data)
                  });
                  toast.success('עודכן');
                } else {
                  await fetchJson(`/api/questionnaires/${id}/questions`, {
                    method: 'POST',
                    body: JSON.stringify(data)
                  });
                  toast.success('נוצר');
                }
                setCreating(false);
                setEditing(null);
                refresh();
              } catch (err: unknown) {
                const e = err as { message?: string };
                toast.error(
                  e.message === 'key_exists' ? 'מפתח כבר קיים בשאלון' : e.message || 'שגיאה'
                );
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
